import React, { useEffect, useRef } from "react";
import * as echarts from "echarts";

interface GaugeProps {
  darkMode?: boolean;
  value?: number;
}

const GaugeChart = ({ darkMode, value }: GaugeProps) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.EChartsType | null>(null);

  useEffect(() => {
    const ranges = [
      { min: 0, max: 20, label: "Worst", color: "#FF6E76" },
      { min: 20, max: 40, label: "Bad", color: "#FDDD60" },
      { min: 40, max: 60, label: "Average", color: "#58D9F9" },
      { min: 60, max: 80, label: "Good", color: "#7CFFB2" },
      { min: 80, max: 100, label: "Better", color: "#9E7CFF" },
      { min: 100, max: 120, label: "Best", color: "#2ECC71" },
    ];

    if (!chartRef.current) return;

    if (chartInstance.current) {
      chartInstance.current.dispose();
    }

    chartInstance.current = echarts.init(
      chartRef.current,
      darkMode ? "dark" : "light",
    );

    const option = {
      backgroundColor: darkMode ? "" : "#ffffff",
      series: [
        {
          type: "gauge",
          startAngle: 180,
          top: 70,
          endAngle: 0,
          center: ["50%", "90%"],
          radius: "170%",
          min: 0,
          max: 120,
          axisLine: {
            lineStyle: {
              width: 30,
              color: ranges.map((r) => [r.max / 120, r.color]),
            },
          },
          pointer: {
            show: true,
            length: "55%",
            width: 6,
            offsetCenter: [0, "-10%"],
            itemStyle: {
              color: "auto",
            },
          },
          axisTick: { show: false },
          splitLine: { show: false },
          splitNumber: 12,
          axisLabel: {
            distance: -5,
            rotate: "tangential",
            fontSize: 14,
            color: "#000",
            formatter: (val: number) => {
              const range = ranges.find((r) => val > r.min && val <= r.max);
              const midpoint = range && (range.min + range.max) / 2;
              return val === midpoint ? range && range.label : "";
            },
          },
          title: { show: false },
          data: [{ value }],
        },
      ],
    };

    chartInstance.current.setOption(option);

    const handleResize = () => chartInstance.current?.resize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      chartInstance.current?.dispose();
    };
  }, [darkMode, value]);

  return <div ref={chartRef} className="w-full h-full" />;
};

export default React.memo(GaugeChart);
